<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { CustomButton } from '@/components/common/';
import { ButtonType } from '@/components';
import InputText from 'primevue/inputtext';
import Badge from 'primevue/badge';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  description: {
    type: String
  },
  showBack: {
    type: Boolean,
    default: false
  },
  showEdit: {
    type: Boolean,
    default: false
  },
  edit: {
    type: Boolean,
    default: false
  },
  locked: {
    type: Boolean,
    default: false
  },
  archived: {
    type: Boolean,
    default: false
  },
  headerInEditMode: {
    type: Boolean,
    default: false
  }
});

const titleText = ref(props.title);

const titleTextProp = computed({
  get() {
    return props.title;
  },
  set(value) {
    titleText.value = value;
  }
});
const editMode = ref(false);

watch(
  () => props.headerInEditMode,
  (newVal: boolean) => {
    editMode.value = newVal;
  }
);

const onEdit = () => {
  editMode.value = !editMode.value;
};

const onCancel = () => {
  titleText.value = props.title;
  editMode.value = !editMode.value;
};

const emits = defineEmits(['onBackClick', 'onTitleSave']);

const onTitleSave = () => {
  emits('onTitleSave', titleText.value.trim());
  editMode.value = !editMode.value;
};

const onBackClick = () => {
  emits('onBackClick');
};

const emptyTitle = computed(() => titleText.value.trim() === '');
</script>

<template>
  <div class="header">
    <CustomButton text="Back"
      :icon="{ style: 'far', icon: 'chevron-left' }"
      class="back-button"
      v-if="showBack"
      @onClick="onBackClick"
      :type="ButtonType.WITHOUT_OUTLINE"/>
    <div class="header-content">
      <div class="header-title">
        <div class="header-edit">
          <h1 v-if="!editMode">{{ title }}</h1>
          <Badge value="LOCKED"
            v-if="locked"
            class="locked"
            severity="contrast"/>
          <Badge value="ARCHIVED"
            v-if="archived"
            class="locked"
            severity="contrast"/>
          <InputText id="header"
            v-model="titleTextProp"
            type="text"
            v-if="editMode"
            class="header-text-input"/>
          <CustomButton text="Edit"
            :type="ButtonType.PRIMARY_WITHOUT_OUTLINE"
            class="button edit-button"
            @onClick="onEdit"
            v-if="!editMode && showEdit"
            :margin-left="16"/>
          <div class="save-buttons" v-if="editMode">
            <CustomButton text="Save"
              :type="ButtonType.POSITIVE"
              class="button"
              @onClick="onTitleSave"
              :disabled="emptyTitle"
              :margin-left="16"/>
            <CustomButton text="Cancel"
              :type="ButtonType.OUTLINED"
              class="button"
              @onClick="onCancel"
              :margin-left="16"/>
          </div>
        </div>
        <h3 class="description" v-if="description">{{ description }}</h3>
      </div>
      <div><slot name="headerButtons"></slot></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.header {
  margin-bottom: 40px;
}
.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
h1 {
  font-size: 1.5rem;
  font-weight: 700;
  border: 1px solid transparent;
}
.locked {
  margin-left: 8px;
}
.p-badge {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2px;
  background-color: #354a4c;
  border-radius: 4px;
}
.edit-button {
  visibility: hidden;
}
.header-edit {
  display: flex;
  align-items: center;

  &:hover {
    .edit-button {
      visibility: visible;
    }
  }
}
.header-text-input {
  font-size: 1.5rem;
  font-weight: 700;
  height: 40px;
  margin-left: -12px;
}
.button {
  height: 32px;
}
.save-buttons {
  display: flex;
  align-items: center;
}
.description {
  font-size: 14px;
  line-height: 38px;
  font-weight: 400;
  color: #333333;
}
.back-button {
  margin-left: -12px;
  margin-bottom: 8px;
}
</style>
